/**
 * Cart drawer CEP shipping estimator (Brazil).
 * ViaCEP + Shopify /cart/shipping_rates.json — no jQuery.
 * Rates are display-only (checkout recalculates; no selection).
 */
(() => {
  const STORAGE_KEY = 'studio-athletic-cart-shipping-zip';

  const UF_TO_PROVINCE = {
    AC: 'Acre',
    AL: 'Alagoas',
    AM: 'Amazonas',
    AP: 'Amapá',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MG: 'Minas Gerais',
    MS: 'Mato Grosso do Sul',
    MT: 'Mato Grosso',
    PA: 'Pará',
    PB: 'Paraíba',
    PE: 'Pernambuco',
    PI: 'Piauí',
    PR: 'Paraná',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RO: 'Rondônia',
    RR: 'Roraima',
    RS: 'Rio Grande do Sul',
    SC: 'Santa Catarina',
    SE: 'Sergipe',
    SP: 'São Paulo',
    TO: 'Tocantins',
  };

  const I18N = {
    invalidZip: 'Digite um CEP válido',
    zipNotFound: 'CEP não encontrado',
    zipUnavailable: 'Consulta de CEP indisponível no momento',
    noRates: 'Não há frete disponível para este CEP',
    ratesError: 'Não foi possível calcular o frete. Tente novamente.',
    calculating: 'Calculando…',
    deliveryHours: 'até 2 horas úteis',
    deliveryDay: '1 dia útil',
    deliveryDays: (n) => `${n} dias úteis`,
  };

  function digitsOnly(value) {
    return String(value || '').replace(/\D/g, '');
  }

  function maskCep(value) {
    const digits = digitsOnly(value).slice(0, 8);
    if (digits.length > 5) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return digits;
  }

  function formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(amount);
    } catch {
      return `R$ ${amount.toFixed(2).replace('.', ',')}`;
    }
  }

  /**
   * @param {{ delivery_days?: number[] }} rate
   */
  function formatDelivery(rate) {
    const days = rate.delivery_days;
    if (!Array.isArray(days) || !days.length || days[0] == null) return '';
    const n = Number(days[0]);
    if (Number.isNaN(n)) return '';
    if (n === 0) return I18N.deliveryHours;
    if (n === 1) return I18N.deliveryDay;
    return I18N.deliveryDays(n);
  }

  class CartShippingCalculator extends HTMLElement {
    /** @type {HTMLInputElement | null} */
    #zip = null;
    /** @type {HTMLButtonElement | null} */
    #submit = null;
    /** @type {HTMLElement | null} */
    #status = null;
    /** @type {HTMLElement | null} */
    #rates = null;
    /** @type {AbortController | null} */
    #abort = null;
    /** @type {((event: Event) => void) | null} */
    #onInput = null;
    /** @type {((event: Event) => void) | null} */
    #onSubmit = null;
    /** @type {((event: KeyboardEvent) => void) | null} */
    #onKeydown = null;

    connectedCallback() {
      this.#zip = this.querySelector('[data-shipping-zip]');
      this.#submit = this.querySelector('[data-shipping-submit]');
      this.#status = this.querySelector('[data-shipping-status]');
      this.#rates = this.querySelector('[data-shipping-rates]');

      if (!this.#zip || !this.#submit) return;

      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved && !this.#zip.value) {
        this.#zip.value = maskCep(saved);
      }

      this.#onInput = (event) => {
        const el = /** @type {HTMLInputElement} */ (event.target);
        const start = el.selectionStart;
        const before = el.value.length;
        el.value = maskCep(el.value);
        const diff = el.value.length - before;
        if (typeof start === 'number') {
          el.setSelectionRange(start + diff, start + diff);
        }
      };

      this.#onSubmit = (event) => {
        event.preventDefault();
        this.#calculate();
      };

      this.#onKeydown = (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.#calculate();
        }
      };

      this.#zip.addEventListener('input', this.#onInput);
      this.#zip.addEventListener('keydown', this.#onKeydown);
      this.#submit.addEventListener('click', this.#onSubmit);
    }

    disconnectedCallback() {
      this.#abort?.abort();
      if (this.#zip && this.#onInput) this.#zip.removeEventListener('input', this.#onInput);
      if (this.#zip && this.#onKeydown) this.#zip.removeEventListener('keydown', this.#onKeydown);
      if (this.#submit && this.#onSubmit) this.#submit.removeEventListener('click', this.#onSubmit);
    }

    /**
     * @param {string} message
     * @param {string} [state]
     */
    #setStatus(message, state = '') {
      if (!this.#status) return;
      if (!message) {
        this.#status.hidden = true;
        this.#status.textContent = '';
        this.#status.removeAttribute('data-state');
        return;
      }
      this.#status.hidden = false;
      this.#status.textContent = message;
      if (state) this.#status.setAttribute('data-state', state);
      else this.#status.removeAttribute('data-state');
    }

    /**
     * @param {Array<{name: string, price: string|number, delivery_days?: number[]}>} rates
     */
    #setRates(rates) {
      if (!this.#rates) return;
      this.#rates.innerHTML = '';
      if (!rates?.length) {
        this.#rates.hidden = true;
        return;
      }

      const frag = document.createDocumentFragment();
      for (const rate of rates) {
        const li = document.createElement('li');
        li.className = 'cart-shipping-calculator__rate';

        const main = document.createElement('div');
        main.className = 'cart-shipping-calculator__rate-main';

        const name = document.createElement('span');
        name.className = 'cart-shipping-calculator__rate-name';
        name.textContent = rate.name || '';

        main.appendChild(name);

        const delivery = formatDelivery(rate);
        if (delivery) {
          const eta = document.createElement('span');
          eta.className = 'cart-shipping-calculator__rate-eta';
          eta.textContent = delivery;
          main.appendChild(eta);
        }

        const priceEl = document.createElement('span');
        priceEl.className = 'cart-shipping-calculator__rate-price';
        const priceNum = Number(rate.price);
        priceEl.textContent =
          priceNum === 0 ? 'Grátis' : formatMoney(Math.round(priceNum * 100));

        li.appendChild(main);
        li.appendChild(priceEl);
        frag.appendChild(li);
      }

      this.#rates.appendChild(frag);
      this.#rates.hidden = false;
    }

    async #calculate() {
      if (!this.#zip) return;

      const zipMasked = maskCep(this.#zip.value);
      const zipDigits = digitsOnly(zipMasked);
      this.#zip.value = zipMasked;

      if (zipDigits.length !== 8) {
        this.#setRates([]);
        this.#setStatus(I18N.invalidZip, 'error');
        return;
      }

      sessionStorage.setItem(STORAGE_KEY, zipMasked);
      this.#abort?.abort();
      this.#abort = new AbortController();
      const { signal } = this.#abort;

      this.dataset.loading = '';
      this.#setRates([]);
      this.#setStatus(I18N.calculating);

      try {
        const viaCepRes = await fetch(`https://viacep.com.br/ws/${zipDigits}/json/`, { signal });
        if (!viaCepRes.ok) throw new Error('viacep');
        const viaCep = await viaCepRes.json();
        if (viaCep.erro) {
          this.#setStatus(I18N.zipNotFound, 'error');
          return;
        }

        const province = UF_TO_PROVINCE[String(viaCep.uf || '').toUpperCase()];
        if (!province) {
          this.#setStatus(I18N.zipNotFound, 'error');
          return;
        }

        const params = new URLSearchParams({
          'shipping_address[zip]': zipMasked,
          'shipping_address[country]': 'Brazil',
          'shipping_address[province]': province,
        });

        const ratesRes = await fetch(`/cart/shipping_rates.json?${params.toString()}`, {
          headers: { Accept: 'application/json' },
          signal,
        });

        if (!ratesRes.ok) {
          this.#setStatus(I18N.ratesError, 'error');
          return;
        }

        const data = await ratesRes.json();
        const rates = data.shipping_rates || [];
        if (!rates.length) {
          this.#setStatus(I18N.noRates, 'error');
          return;
        }

        this.#setStatus('');
        this.#setRates(rates);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        this.#setStatus(
          String(error?.message || '').includes('viacep') ? I18N.zipUnavailable : I18N.ratesError,
          'error'
        );
      } finally {
        delete this.dataset.loading;
      }
    }
  }

  if (!customElements.get('cart-shipping-calculator')) {
    customElements.define('cart-shipping-calculator', CartShippingCalculator);
  }
})();
